/* eslint-disable react-hooks/exhaustive-deps */
// -----------------------------------------------------------------------------
// UpNextCardV2  (Phase 4a — v2 animation variant)
//
// Drop-in replacement for UpNextCard with two additions:
//   1. Mood gradient — radial from top-left in the action color at 8% opacity,
//      fades in 600ms after card mounts (200ms delay).
//   2. Action-typed icon micro-animation — 1.2s CSS loop, paused on hover,
//      disabled when prefers-reduced-motion is set.
//
// Animation class per action type:
//   EMAIL_SENT / SEND_TREATMENT_PLAN / SEND_REVIEW_REQUEST  → envelope-flap
//   PHONE_CALLED / LOG_PHONE_CALL                           → ring-wobble
//   SCHEDULE_REMINDER                                       → bell-shake
//   SEND_PAYMENT_LINK / CURRENCY_AMOUNT / PAYMENT_METHOD    → coin-flip
//   WHATSAPP_SENT / SMS_SENT                                → pulse-scale
//   SEND_CONSENT_FORM / FREE_TEXT / ADD_NOTE                → pen-tilt
//   (all others)                                            → scale-breathe-v2
//
// The class name is exposed as `data-animation` on the icon wrapper for tests.
// previewMode prop: disabled button, "PREVIEW · UPCOMING" eyebrow, no skip.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useEffect, useMemo, useRef } from 'react';
// due_at can be DRF "DD-MM-YYYY HH:mm:ss.SSSSSSZ" (standalone tasks) — moment()/
// new Date() without a format token render "Invalid date" on it. parseApiDate
// auto-detects DRF vs ISO and returns a dayjs (same format tokens as moment).
import dayjs from 'dayjs';
import { parseApiDate } from 'src/utils/taskDateUtils';
import {
    Box, Typography, Button, Stack, Tooltip, useMediaQuery,
    CircularProgress, IconButton, Dialog, DialogContent, DialogActions,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from 'src/components/Iconify';
import AssignedToLead from 'src/components/AssignedToLead';
import { SUB_LABEL } from 'src/styles/stepFormStyles';
import usePracticeStaff from 'src/hooks/usePracticeStaff';
import usePracticeTaskDefaults from 'src/sections/tasks/usePracticeTaskDefaults';
import { callApi } from 'src/utils/apiCall';
import {
    diffAssignees,
    isNoopDelta,
    buildWritePlan,
    getSkipReason,
    isSkippedForClosedStatus,
    skipMessage,
} from 'src/utils/taskAssigneeDelta';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { CRM_PRIMARY_BUTTON_SX } from 'src/styles/crmButtons';
import { ACTION_META, DEFAULT_ACTION_META } from '../actionMeta';
import { EMAIL_ACTION_TYPES } from '../ActionPanel';
import RescheduleDialog from './RescheduleDialog';
import { STANDALONE_ACTIONABLE_TYPES } from '../../standaloneTaskConstants';

const EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';
const APPEAR_MS = 680;

// ---------------------------------------------------------------------------
// Bug fix (pre-prod, practice 36): a bulk-assignees 400 for a deactivated
// account ("One or more user_ids are not assignable members",
// invalid_user_ids: [651, 1014]) previously fell through to fail()'s generic
// 'Failed to update assignees', which the user never actually saw surfaced
// anywhere meaningful mid-flow — this builds a message that's always
// non-empty and calls out the specific reason when the server names it.
// ---------------------------------------------------------------------------
function buildAssignFailureMessage(data) {
    let message = data?.detail || 'Could not assign these users to this task.';
    if (Array.isArray(data?.invalid_user_ids) && data.invalid_user_ids.length > 0) {
        message += ' Some listed users are no longer active.';
    }
    return message;
}

// ---------------------------------------------------------------------------
// Action type → animation class
// ---------------------------------------------------------------------------

const ICON_ANIMATION_CLASS = {
    EMAIL_SENT:           'envelope-flap',
    SEND_TREATMENT_PLAN:  'envelope-flap',
    SEND_REVIEW_REQUEST:  'envelope-flap',
    PHONE_CALLED:         'ring-wobble',
    LOG_PHONE_CALL:       'ring-wobble',
    SCHEDULE_REMINDER:    'bell-shake',
    SEND_PAYMENT_LINK:    'coin-flip',
    CURRENCY_AMOUNT:      'coin-flip',
    PAYMENT_METHOD:       'coin-flip',
    WHATSAPP_SENT:        'pulse-scale',
    SMS_SENT:             'pulse-scale',
    SEND_CONSENT_FORM:    'pen-tilt',
    FREE_TEXT:            'pen-tilt',
    ADD_NOTE:             'pen-tilt',
};
const DEFAULT_ANIMATION_CLASS = 'scale-breathe-v2';

export function getIconAnimationClass(itemType) {
    return ICON_ANIMATION_CLASS[itemType] || DEFAULT_ANIMATION_CLASS;
}

// ---------------------------------------------------------------------------
// Keyframe definitions per animation class
// ---------------------------------------------------------------------------

const KEYFRAME_DEFS = {
    'envelope-flap': {
        '@keyframes envelope-flap': {
            '0%, 100%': { transform: 'rotateY(0deg)' },
            '50%':       { transform: 'rotateY(8deg)' },
        },
    },
    'ring-wobble': {
        '@keyframes ring-wobble': {
            '0%, 100%': { transform: 'rotate(0deg)' },
            '25%':       { transform: 'rotate(-6deg)' },
            '75%':       { transform: 'rotate(6deg)' },
        },
    },
    'bell-shake': {
        '@keyframes bell-shake': {
            '0%, 100%': { transform: 'translateX(0) rotateZ(0deg)' },
            '25%':       { transform: 'translateX(-2px) rotateZ(-3deg)' },
            '75%':       { transform: 'translateX(2px) rotateZ(3deg)' },
        },
    },
    'coin-flip': {
        '@keyframes coin-flip': {
            '0%':   { transform: 'rotateY(0deg)' },
            '50%':  { transform: 'rotateY(180deg)' },
            '100%': { transform: 'rotateY(360deg)' },
        },
    },
    'pulse-scale': {
        '@keyframes pulse-scale': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%':       { transform: 'scale(1.08)' },
        },
    },
    'pen-tilt': {
        '@keyframes pen-tilt': {
            '0%, 100%': { transform: 'rotateZ(0deg)' },
            '25%':       { transform: 'rotateZ(-4deg)' },
            '75%':       { transform: 'rotateZ(4deg)' },
        },
    },
    'scale-breathe-v2': {
        '@keyframes scale-breathe-v2': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%':       { transform: 'scale(1.04)' },
        },
    },
};

export function getIconAnimSx(animClass, reducedMotion) {
    if (reducedMotion) return {};
    const kf = KEYFRAME_DEFS[animClass] || KEYFRAME_DEFS['scale-breathe-v2'];
    const kfName = KEYFRAME_DEFS[animClass] ? animClass : 'scale-breathe-v2';
    return {
        ...kf,
        animation: `${kfName} 1.2s ${EASING} infinite`,
        '&:hover': { animationPlayState: 'paused' },
    };
}

// ---------------------------------------------------------------------------
// Primary button labels (same as v1)
// ---------------------------------------------------------------------------

const ACTION_BUTTON_LABELS = {
    EMAIL_SENT:          'Compose email',
    WHATSAPP_SENT:       'Send WhatsApp',
    SMS_SENT:            'Send SMS',
    PHONE_CALLED:        'Log call',
    LOG_PHONE_CALL:      'Log call',
    SEND_CONSENT_FORM:   'Send consent',
    SEND_REVIEW_REQUEST: 'Send review request',
    SEND_PAYMENT_LINK:   'Send payment link',
    SEND_TREATMENT_PLAN: 'Send treatment plan',
    ADD_NOTE:            'Add note',
    SCHEDULE_REMINDER:   'Set reminder',
    LEAD_NEXT_ACTION:    'Log outcome & next step',
    BOOK_APPOINTMENT:    'Book appointment',
    CONFIRMATION:        'Mark confirmed',
    UPLOAD_DOCUMENT:     'Upload document',
    LINKED_CHECKLIST:    'Open checklist',
    FREE_TEXT:           'Enter text',
    MULTIPLE_CHOICE:     'Choose option',
    YES_NO_TOGGLE:       'Mark done',
    CURRENCY_AMOUNT:     'Enter amount',
    PAYMENT_METHOD:      'Set method',
    STAFF_PICKER:        'Assign staff',
    DATE_FIELD:          'Set date',
    SIMPLE_DATE:         'Set Date',
};

// ---------------------------------------------------------------------------
// Relative, scannable due label for the hero card — matches ChecklistMiniGrid's
// short style (Today / Tomorrow / Yesterday / "Wed 8 Jul"), plus the time.
// ---------------------------------------------------------------------------
function formatDueRelative(dueStr) {
    const due = parseApiDate(dueStr);
    if (!due || !due.isValid()) return '';
    const now = dayjs();
    const t = due.format('HH:mm');
    if (due.isSame(now, 'day')) return `Today · ${t}`;
    if (due.isSame(now.add(1, 'day'), 'day')) return `Tomorrow · ${t}`;
    if (due.isSame(now.subtract(1, 'day'), 'day')) return `Yesterday · ${t}`;
    return `${due.format('ddd D MMM')} · ${t}`;
}

// ---------------------------------------------------------------------------
// Eyebrow helper (same logic as v1)
// ---------------------------------------------------------------------------

// Exported so other surfaces showing the same task's overdue state (e.g. the
// New-lead table's Next Task cell — see src/utils/nextTaskStatus.js) can match
// this card's "Overdue by X" number exactly instead of independently computing
// a calendar-day diff, which can disagree with this ROLLING 24h-floor rule by
// up to a day depending on time-of-day (2026-10-02: cell said "141 days",
// card said "140 days" for the same task).
export function getOverdueByLabel(due, now = new Date()) {
    const diffMs = now - due;
    const diffHours = Math.floor(diffMs / 3_600_000);
    const diffDays = Math.floor(diffHours / 24);
    return diffDays >= 1
        ? `${diffDays} day${diffDays === 1 ? '' : 's'}`
        : `${Math.max(diffHours, 1)} hour${diffHours === 1 ? '' : 's'}`;
}

function getEyebrow(item) {
    if (!item?.due_at) return { label: 'NEXT ACTION', color: '#6e6e73' };
    const now = new Date();
    const due = parseApiDate(item.due_at)?.toDate() || new Date(item.due_at);
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    // Day-based overdue: a task is OVERDUE only once its due date is a PRIOR
    // calendar day. A task due any time TODAY reads "Due today", never overdue
    // (previously `due < now` flagged an earlier-today task as overdue).
    if (due < startOfToday) {
        return { label: 'OVERDUE', color: '#FF5630', overdueBy: getOverdueByLabel(due, now) };
    }
    if (due <= endOfToday) return { label: 'DUE TODAY', color: '#FFAB00' };
    return { label: 'NEXT ACTION', color: '#6e6e73' };
}

// ---------------------------------------------------------------------------
// Assignee profile-heads — same visual as the reminder UI (AssignedToLead),
// rendered directly on the card. Each toggle PATCHes the EXISTING task's
// assignees, mirroring TaskAssigneeRow's persistence:
//     PATCH /api/v1/practices/{practiceId}/tasks/{taskId}/
//     body:  { assignees: [{ practice_user_id: pu.id }] }
//
// Seeding: the task-checklists API returns assignees as { id: user_id, name }.
// usePracticeStaff returns PracticeUser objects where pu.user.id === user_id,
// so we cross-reference to hydrate the selected set with full PracticeUser
// objects (same approach as TaskAssigneeRow) — this drives AssignedToLead's
// selected ring/check state.
// ---------------------------------------------------------------------------
// Cap the inline avatar row at 6; the rest sit behind a "+N" popover.
const ASSIGNEE_VISIBLE_CAP = 6;

export function TaskAssigneeHeads({ practiceId, taskId, initialAssignees, leadId, onLeadTasksReassigned }) {
    const { assignableUsers, isLoaded } = usePracticeStaff();
    const [saving, setSaving] = useState(false);
    // null = not yet interacted; fall back to the hydrated initial selection.
    const [selected, setSelected] = useState(null);
    // "Assign to all" → propagate-to-lead flow state.
    const { defaults, update: updateTaskDefaults } = usePracticeTaskDefaults(practiceId);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [rememberOpen, setRememberOpen] = useState(false);
    const [leadSyncing, setLeadSyncing] = useState(false);
    const [rememberSaving, setRememberSaving] = useState(false);
    // Review fix #3: whole-pipeline in-flight guard (persist → lead bulk call →
    // dialog resolution). Mirrored in a ref so a second click arriving in the
    // SAME synchronous batch as the first (before React re-renders the disabled
    // button) is still rejected — state alone isn't visible to that closure yet.
    const [assignAllInFlight, setAssignAllInFlightState] = useState(false);
    const assignAllInFlightRef = useRef(false);
    const setAssignAllInFlight = (value) => {
        assignAllInFlightRef.current = value;
        setAssignAllInFlightState(value);
    };
    // Review fix #1: flipped by a cancel (No / dialog-1 onClose) so a Yes bulk
    // call that was already in flight knows, when it resolves, not to reopen
    // dialog 2 — independent of whatever the "No" button's disabled state
    // happened to be at the moment of the click.
    const cancelledRef = useRef(false);
    // Review fix #2: this component can remount mid-flight (the card swaps
    // when the task advances) — guard every setState that runs after an
    // `await` in the assign-all pipeline.
    const isMountedRef = useRef(true);

    // ---------------------------------------------------------------------
    // Follow-up 2 — settle prompt after individual avatar toggles.
    //
    // Individual toggles (AssignedToLead → setAssignTo) save one at a time and
    // historically never offered to propagate to the lead's other open tasks
    // — only the "Assign to all" button did. This adds a 2s idle timer after
    // each successful toggle write: if the selection has drifted from the
    // start of the current editing "burst" AND the task has a lead, either
    // silently propagate (practice pref on) or show a small inline strip
    // asking (pref off). See Follow-up 2 in 2026-09-10-assign-all-lead-tasks-plan.md.
    // ---------------------------------------------------------------------
    const settleTimerRef = useRef(null);
    // ids of the selection at the FIRST toggle since the last prompt answer
    // (or mount). null = no burst in progress yet.
    const burstStartSelectionRef = useRef(null);
    const [settleStripOpen, setSettleStripOpen] = useState(false);

    useEffect(() => () => {
        isMountedRef.current = false;
        if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    }, []);

    const hydratedInitial = useMemo(() => {
        if (!isLoaded || !initialAssignees?.length) return [];
        const userIdSet = new Set(initialAssignees.map((a) => a.id));
        return assignableUsers.filter((pu) => userIdSet.has(pu.user?.id));
    }, [isLoaded, initialAssignees, assignableUsers]);

    const effectiveSelected = selected !== null ? selected : hydratedInitial;

    // Persist on every toggle (no Save button — the profile-heads ARE the control).
    //
    // Writes a DELTA, not the whole list. This used to PATCH
    // { assignees: [...everyone currently selected] }, which the API applies as
    // REPLACE semantics — anyone absent from that array is deleted. The array
    // came from `selected`, which resets, falling back to `hydratedInitial`,
    // which filters through `assignableUsers` and therefore EXCLUDES ADMIN. A
    // task whose only row is the admin placeholder (70 of practice 19's 71
    // unassigned tasks) hydrated as EMPTY, so one click sent a one-element
    // array and deleted everybody else. Prod, 30d: 1,476 add-then-remove events
    // inside 120 seconds across 782 tasks, the same actor on both sides.
    //
    // With a delta, a stale base can only cause a MISSING ADD, never a spurious
    // removal — the failure mode degrades from "silently deleted seven people"
    // to "you may need to click again". See src/utils/taskAssigneeDelta.js.
    // Returns whether the write succeeded (true = success or no-op, false =
    // failed) — used by the "Assign to all" click handler below to decide
    // whether it's safe to propagate the same assignees to the lead's other
    // open tasks.
    const persist = async (nextSelected) => {
        const previousSelected = effectiveSelected;
        setSelected(nextSelected);
        if (!taskId || !practiceId) return false;

        const delta = diffAssignees(previousSelected, nextSelected);
        if (isNoopDelta(delta)) return true;

        // Roll the heads back only while NOTHING has been written. Once a step
        // has landed the server has moved, and reverting the UI would display a
        // state that is no longer true — show the error and let the next
        // refetch reconcile.
        let wrote = false;
        const fail = (message) => {
            dispatch(updateError({ success: false, message }));
            if (!wrote) setSelected(previousSelected);
        };

        setSaving(true);
        try {
            const plan = buildWritePlan(delta);
            let closedTask = false;

            /* eslint-disable no-await-in-loop */
            for (let i = 0; i < plan.length; i += 1) {
                const step = plan[i];
                const res = await callApi(
                    `/api/v1/practices/${practiceId}/tasks/bulk-assignees/`,
                    'post',
                    {
                        task_ids: [taskId],
                        mode: step.mode,
                        user_ids: step.userIds,
                        preview: false,
                    },
                );
                if (!res.ok) {
                    fail(buildAssignFailureMessage(res.data));
                    return false;
                }
                const reason = getSkipReason(res.data, taskId);
                if (reason) {
                    if (isSkippedForClosedStatus(res.data, taskId)) {
                        // bulk-assignees only mutates pending / in_progress. The
                        // PATCH it replaces had no such limit, so without this
                        // branch assigning on a COMPLETED task would silently
                        // stop working — a new bug in place of the old one.
                        closedTask = true;
                        break;
                    }
                    // not_found / no_permission come back as skips, NOT errors.
                    // Without this they would pass silently as success.
                    fail(skipMessage(reason) || 'Failed to update assignees');
                    return false;
                }
                wrote = true;
            }
            /* eslint-enable no-await-in-loop */

            if (closedTask) {
                const res = await callApi(
                    `/api/v1/practices/${practiceId}/tasks/${taskId}/`,
                    'patch',
                    { assignees: nextSelected.map((pu) => ({ practice_user_id: pu.id })) },
                );
                if (!res.ok) {
                    fail(buildAssignFailureMessage(res.data));
                    return false;
                }
            }
            return true;
        } catch (err) {
            fail(err.message || 'Something went wrong');
            return false;
        } finally {
            setSaving(false);
        }
    };

    // ---------------------------------------------------------------------
    // "Assign to all" → propagate to the lead's other open tasks.
    //
    // After the click's own PATCH succeeds, if this task belongs to a lead:
    //   - practice pref ON  → silently replace assignees on every other open
    //     task of the lead, then toast success.
    //   - practice pref OFF → ask (dialog 1); on Yes, do the same bulk call,
    //     then ask whether to remember the choice for this practice (dialog 2).
    // ---------------------------------------------------------------------
    const runBulkLeadAssign = async ({ userIds, silent, message } = {}) => {
        if (!practiceId || !leadId) return true;
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/tasks/bulk-assignees/`,
                'post',
                {
                    lead_id: leadId,
                    exclude_task_ids: [taskId],
                    mode: 'replace',
                    user_ids: userIds || assignableUsers.map((pu) => pu.id),
                    preview: false,
                },
            );
            if (!res.ok) {
                dispatch(updateError({
                    success: false,
                    message: res?.data?.detail || "Failed to update this lead's other tasks",
                }));
                return false;
            }
            if (onLeadTasksReassigned) onLeadTasksReassigned();
            if (!silent) {
                dispatch(updateError({
                    success: true,
                    message: message || "Assigned everyone to this lead's other open tasks",
                }));
            }
            return true;
        } catch (err) {
            dispatch(updateError({
                success: false,
                message: err.message || "Failed to update this lead's other tasks",
            }));
            return false;
        }
    };

    const handleAssignAllClick = async () => {
        // Coordinator fix #2: the modal Assign-to-all flow must never be
        // ambushed by a settle timer/strip armed by an earlier individual
        // toggle — clear it before anything else.
        clearSettleTimer();
        setSettleStripOpen(false);
        burstStartSelectionRef.current = null;

        // Review fix #3: ref check, not state — immune to same-batch double clicks.
        if (assignAllInFlightRef.current) return;
        setAssignAllInFlight(true);

        const ok = await persist(assignableUsers);
        if (!isMountedRef.current) return; // Review fix #2

        if (!ok || !taskId || !leadId) {
            setAssignAllInFlight(false);
            return;
        }

        if (defaults?.assign_all_applies_to_lead_tasks) {
            await runBulkLeadAssign();
            if (!isMountedRef.current) return; // Review fix #2
            setAssignAllInFlight(false);
            return;
        }

        cancelledRef.current = false;
        setConfirmOpen(true);
        // Stays in-flight until dialog 1 (No/cancel) or dialog 2 (Not now/Yes)
        // resolves the pipeline — see handleConfirmNo / handleRememberNo / handleRememberYes.
    };

    // ---------------------------------------------------------------------
    // Individual avatar toggle → save immediately (persist), then arm/re-arm
    // the 2s settle timer. Distinct from handleAssignAllClick: only a toggle
    // through this path starts the timer, and only once persist() resolves
    // successfully.
    // ---------------------------------------------------------------------
    const idsOf = (list) => (list || []).map((pu) => pu.id);
    const sameIdSet = (a, b) => {
        const setA = new Set(a || []);
        const setB = new Set(b || []);
        if (setA.size !== setB.size) return false;
        return [...setA].every((id) => setB.has(id));
    };

    const clearSettleTimer = () => {
        if (settleTimerRef.current) {
            clearTimeout(settleTimerRef.current);
            settleTimerRef.current = null;
        }
    };

    const armSettleTimer = (nextSelected) => {
        clearSettleTimer();
        settleTimerRef.current = setTimeout(() => {
            settleTimerRef.current = null;
            if (!isMountedRef.current) return;

            const startIds = burstStartSelectionRef.current;
            const currentIds = idsOf(nextSelected);
            if (!leadId || sameIdSet(startIds, currentIds)) {
                burstStartSelectionRef.current = null;
                return;
            }

            if (defaults?.assign_all_applies_to_lead_tasks) {
                burstStartSelectionRef.current = null;
                // Coordinator fix #1: never fire (or queue) the silent settle
                // POST while another assign-all pipeline (button click or
                // strip Yes) is already in flight — skip this burst entirely
                // rather than stacking a second lead-scoped call.
                if (assignAllInFlightRef.current) return;
                setAssignAllInFlight(true);
                // Coordinator fix #3: success toast worded for the PARTIAL
                // selection this burst carries — distinct from the "everyone"
                // wording used by the Assign-to-all button's own pref-ON path.
                runBulkLeadAssign({
                    userIds: currentIds,
                    silent: false,
                    message: "Applied these assignees to this lead's other open tasks",
                }).finally(() => {
                    if (isMountedRef.current) setAssignAllInFlight(false);
                });
            } else {
                setSettleStripOpen(true);
                // Burst-start is kept until the strip is answered (Yes/No).
            }
        }, 2000);
    };

    const handleIndividualToggle = async (nextSelected) => {
        // Any new toggle hides an open strip and cancels the pending timer —
        // it gets re-armed below once this toggle's write has landed.
        clearSettleTimer();
        setSettleStripOpen(false);

        if (burstStartSelectionRef.current === null) {
            burstStartSelectionRef.current = idsOf(effectiveSelected);
        }

        const ok = await persist(nextSelected);
        if (!isMountedRef.current || !ok) return;

        armSettleTimer(nextSelected);
    };

    const handleSettleNo = () => {
        clearSettleTimer();
        setSettleStripOpen(false);
        // Don't ask again until a NEW toggle starts a new burst.
        burstStartSelectionRef.current = idsOf(effectiveSelected);
    };

    const handleSettleYes = async () => {
        if (assignAllInFlightRef.current) return;
        setAssignAllInFlight(true);

        const ok = await runBulkLeadAssign({ userIds: idsOf(effectiveSelected), silent: true });
        if (!isMountedRef.current) return;

        setSettleStripOpen(false);
        burstStartSelectionRef.current = null;

        if (ok) {
            cancelledRef.current = false;
            setRememberOpen(true);
        } else {
            setAssignAllInFlight(false);
        }
    };

    const handleConfirmNo = () => {
        // Review fix #1: flip the cancel flag FIRST — synchronous, so a
        // handleConfirmYes already in flight sees it as soon as it checks,
        // regardless of whether this click raced ahead of a re-render.
        cancelledRef.current = true;
        setConfirmOpen(false);
        setAssignAllInFlight(false); // Review fix #3: cancelling ends the pipeline.
    };

    // Review fix #1: Dialog 1 must not be dismissible via backdrop while the
    // Yes bulk call is in flight — an accidental backdrop/Escape shouldn't be
    // treated as "No" mid-request. disableEscapeKeyDown (on the Dialog below)
    // covers Escape; this covers the backdrop-click reason.
    const handleConfirmDialogClose = (event, reason) => {
        if (leadSyncing && reason === 'backdropClick') return;
        handleConfirmNo();
    };

    const handleConfirmYes = async () => {
        setLeadSyncing(true);
        const ok = await runBulkLeadAssign({ silent: true });
        if (!isMountedRef.current) return; // Review fix #2

        setLeadSyncing(false);
        setConfirmOpen(false);
        // Review fix #1: a cancel that landed while this call was in flight
        // (cancelledRef, not the "No" button's disabled state) wins — never
        // reopen dialog 2 after the user already said No.
        if (ok && !cancelledRef.current) {
            setRememberOpen(true);
        } else {
            setAssignAllInFlight(false); // Review fix #3: cancelled/failed — pipeline ends here.
        }
    };

    const handleRememberNo = () => {
        setRememberOpen(false);
        setAssignAllInFlight(false); // Review fix #3: dialog 2 resolved — pipeline ends.
    };

    const handleRememberYes = async () => {
        setRememberSaving(true);
        await updateTaskDefaults({ assign_all_applies_to_lead_tasks: true });
        if (!isMountedRef.current) return; // Review fix #2

        setRememberSaving(false);
        setRememberOpen(false);
        setAssignAllInFlight(false); // Review fix #3: dialog 2 resolved — pipeline ends.
    };

    if (!isLoaded) {
        return <CircularProgress size={18} sx={{ color: '#919EAB' }} />;
    }

    return (
        <Box>
            {/* "ASSIGN TO" label rendered ONCE here, left-aligned, so it lines up with
                the first chip in the row below (the assign-to-all button). The label is
                NOT passed to AssignedToLead — that keeps everything on a single baseline. */}
            <Typography sx={SUB_LABEL}>ASSIGN TO</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', rowGap: '4px' }}>
                {/* Assign to all — first chip, same 30px circle as the avatar heads so
                    it sits in line with them. Deliberately NOT a toggle; unassign-all
                    excluded pending a human decision on whether that flow is needed. */}
                <Tooltip title="Assign to all" placement="top">
                    <span>
                        <IconButton
                            data-testid="assign-all-btn"
                            size="small"
                            onClick={handleAssignAllClick}
                            disabled={saving || !assignableUsers.length || assignAllInFlight}
                            sx={{
                                width: 30,
                                height: 30,
                                bgcolor: '#855CF8',
                                color: '#fff',
                                '&:hover': { bgcolor: '#7248e6' },
                                '&.Mui-disabled': { bgcolor: '#855CF8', opacity: 0.45, color: '#fff' },
                            }}
                        >
                            <Iconify icon="eva:people-fill" sx={{ fontSize: 16 }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <AssignedToLead
                    assignToState={effectiveSelected}
                    setAssignTo={handleIndividualToggle}
                    staffRole="assignee"
                    maxVisible={ASSIGNEE_VISIBLE_CAP}
                />
                {saving && <CircularProgress size={14} sx={{ color: '#919EAB' }} />}
            </Box>

            {/* Inline settle-prompt strip — non-modal, shown 2s after an individual
                avatar toggle settles, only when the practice pref is off and the
                selection has drifted from the start of this editing burst. */}
            {settleStripOpen && (
                <Stack
                    data-testid="assign-all-inline-strip"
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: '4px' }}
                >
                    <Typography sx={{ fontSize: '12px', color: '#6e6e73', letterSpacing: '-0.005em' }}>
                        Apply these assignees to the lead&apos;s other open tasks?
                    </Typography>
                    <Button
                        data-testid="assign-all-inline-no"
                        size="small"
                        onClick={handleSettleNo}
                        sx={{ color: '#6e6e73', textTransform: 'none', fontSize: '12px', fontWeight: 500, minWidth: 0, px: 1 }}
                    >
                        No
                    </Button>
                    <LoadingButton
                        data-testid="assign-all-inline-yes"
                        size="small"
                        variant="contained"
                        disableElevation
                        loading={assignAllInFlight}
                        onClick={handleSettleYes}
                        sx={{ ...CRM_PRIMARY_BUTTON_SX, textTransform: 'none', fontSize: '12px', fontWeight: 600, py: 0.25, px: 1.25, borderRadius: '6px', minWidth: 0 }}
                    >
                        Yes
                    </LoadingButton>
                </Stack>
            )}

            {/* Dialog 1 — offered only when the practice pref is off. Mirrors the
                pause-dialog pattern (ScheduleReminderTaskSection.jsx). */}
            <Dialog
                data-testid="assign-all-lead-dialog"
                open={confirmOpen}
                onClose={handleConfirmDialogClose}
                disableEscapeKeyDown={leadSyncing}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                        Apply to all open tasks?
                    </Typography>
                </Box>
                <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
                    <Typography sx={{ fontSize: '13px', color: '#3c3c43', letterSpacing: '-0.005em' }}>
                        Assign everyone to all other open tasks for this lead as well?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.75, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                    <Button
                        data-testid="assign-all-lead-no"
                        onClick={handleConfirmNo}
                        disabled={leadSyncing}
                        sx={{ color: '#6e6e73', textTransform: 'none', fontSize: '14px', fontWeight: 500 }}
                    >
                        No
                    </Button>
                    <LoadingButton
                        data-testid="assign-all-lead-yes"
                        variant="contained"
                        disableElevation
                        loading={leadSyncing}
                        onClick={handleConfirmYes}
                        sx={{ ...CRM_PRIMARY_BUTTON_SX, textTransform: 'none', fontSize: '14px', fontWeight: 600, py: 0.75, px: 2.5, borderRadius: '8px' }}
                    >
                        Yes, apply to all
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            {/* Dialog 2 — offered only after a successful Yes on dialog 1. */}
            <Dialog
                data-testid="assign-all-remember-dialog"
                open={rememberOpen}
                onClose={handleRememberNo}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                        Remember this choice?
                    </Typography>
                </Box>
                <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
                    <Typography sx={{ fontSize: '13px', color: '#3c3c43', letterSpacing: '-0.005em' }}>
                        Always apply &quot;Assign to all&quot; to every open task for this lead in this
                        practice? Anyone in the practice can turn this off from the Tasks page menu.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.75, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                    <Button
                        data-testid="assign-all-remember-no"
                        onClick={handleRememberNo}
                        sx={{ color: '#6e6e73', textTransform: 'none', fontSize: '14px', fontWeight: 500 }}
                    >
                        Not now
                    </Button>
                    <LoadingButton
                        data-testid="assign-all-remember-yes"
                        variant="contained"
                        disableElevation
                        loading={rememberSaving}
                        onClick={handleRememberYes}
                        sx={{ ...CRM_PRIMARY_BUTTON_SX, textTransform: 'none', fontSize: '14px', fontWeight: 600, py: 0.75, px: 2.5, borderRadius: '8px' }}
                    >
                        Yes, always
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
TaskAssigneeHeads.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    taskId: PropTypes.string,
    /** Raw assignees from task-checklists API: [{ id: user_id, name }] */
    initialAssignees: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    /** Lead this task belongs to — enables the "Assign to all" → propagate-to-lead flow. */
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Called after the lead's other open tasks are successfully reassigned, so the
        caller can refresh its task list. */
    onLeadTasksReassigned: PropTypes.func,
};
TaskAssigneeHeads.defaultProps = {
    practiceId: null,
    taskId: null,
    initialAssignees: [],
    leadId: null,
    onLeadTasksReassigned: null,
};

// ---------------------------------------------------------------------------
// Preview-item assignee picker — identical look to TaskAssigneeHeads
// (AssignedToLead "ASSIGN TO"), but the item has no taskId yet. Selection is
// held in local component state and pushed to the parent's onPreviewAssign on
// every toggle, which spawns the task from template (idempotent) and PATCHes
// the assignees — so the choice is persisted, not faked, with NO backend change
// (reuses the existing /tasks/from-template/ + PATCH /tasks/{id}/ endpoints,
// mirroring how handleChangeDueDate already spawns-then-patches a preview item).
// ---------------------------------------------------------------------------
function PreviewAssigneeHeads({ item, onPreviewAssign }) {
    const { isLoaded } = usePracticeStaff();
    const [selected, setSelected] = useState([]);
    const [saving, setSaving] = useState(false);

    const handleChange = async (nextSelected) => {
        setSelected(nextSelected);
        if (!onPreviewAssign) return;
        setSaving(true);
        try {
            await onPreviewAssign(item, nextSelected);
        } finally {
            setSaving(false);
        }
    };

    if (!isLoaded) {
        return <CircularProgress size={18} sx={{ color: '#919EAB' }} />;
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignedToLead
                label="ASSIGN TO"
                assignToState={selected}
                setAssignTo={handleChange}
                staffRole="assignee"
                maxVisible={ASSIGNEE_VISIBLE_CAP}
            />
            {saving && <CircularProgress size={14} sx={{ color: '#919EAB' }} />}
        </Box>
    );
}
PreviewAssigneeHeads.propTypes = {
    item: PropTypes.object,
    onPreviewAssign: PropTypes.func,
};
PreviewAssigneeHeads.defaultProps = {
    item: null,
    onPreviewAssign: null,
};

// ---------------------------------------------------------------------------

export default function UpNextCardV2({
    item, onActionClick, onSkip, onPause, leadEmail, onChangeDueDate,
    // nextItem / nextEmptyReason / onItemClick / panelOpen are retained in the
    // signature so existing callers don't break, but the "Next up" preview pane
    // (and the two-pane row layout it drove) was removed — the card is now a
    // single, minimal column. Intentionally unused.
    nextItem, nextEmptyReason, onItemClick, panelOpen, // eslint-disable-line no-unused-vars
    practiceId, onPreviewAssign,
    // Change 1: callback wired to handlePanelComplete in the orchestrator.
    // MUST reuse the existing completion path — do NOT create a parallel one.
    onMarkComplete,
    // Assign-to-all → propagate-to-lead flow, forwarded to TaskAssigneeHeads.
    leadId, onLeadTasksReassigned,
}) {
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const [appeared, setAppeared] = useState(false);
    // Change 3: shared RescheduleDialog state — replaces the inline Dialog.
    const [rescheduleOpen, setRescheduleOpen] = useState(false);
    // Wave A Change 2: in-flight loading guard — set true on click, resets on card remount.
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setAppeared(true), 16);
        return () => clearTimeout(t);
    }, []);

    // Change 3: RescheduleDialog handlers
    const handleOpenReschedule = () => setRescheduleOpen(true);
    const handleCloseReschedule = () => setRescheduleOpen(false);
    const handleConfirmReschedule = (newDate, newTitle) => {
        if (onChangeDueDate && item) return onChangeDueDate(item, newDate, newTitle);
        return false;
    };

    if (!item) return null;

    const meta = ACTION_META[item.item_type] || DEFAULT_ACTION_META;
    const eyebrow = getEyebrow(item);
    const title = item.title || item.name || meta.label;
    const isEmailType = EMAIL_ACTION_TYPES.has(item.item_type);
    // Only block when the orchestrator explicitly passes an empty leadEmail string.
    // If leadEmail is undefined (prop absent), the guard is inactive.
    const noEmailBlocked = isEmailType && leadEmail !== undefined && !leadEmail;
    // Standalone tasks only have a meaningful action button for the 3 real action types.
    // A method-less standalone (item_type === 'STANDALONE_TASK') has no panel to open —
    // hide the primary button entirely so there's no dead "Take action" dead-end.
    const showPrimaryButton = !item.isStandaloneTask || STANDALONE_ACTIONABLE_TYPES.has(item.item_type);
    let buttonLabel = ACTION_BUTTON_LABELS[item.item_type] || 'Take action';
    if (noEmailBlocked) buttonLabel = 'Add email first';
    const isOverdue = eyebrow.label === 'OVERDUE';

    const animClass = getIconAnimationClass(item.item_type);
    const iconAnimSx = getIconAnimSx(animClass, reducedMotion);

    // Wave A Change 3: LEAD_NEXT_ACTION requires resolved_action → direct /complete/ returns 400.
    // Hide "Mark as completed" for any type that can't be completed without a payload the button can't supply.
    // 2026-06-13 (Raj): also force CONFIRMATION + YES_NO_TOGGLE through their panel so the
    // user always answers Yes/No (and can add a note) rather than silently auto-completing.
    const requiresActionFirst = ['LEAD_NEXT_ACTION', 'CONFIRMATION', 'YES_NO_TOGGLE'].includes(item.item_type);

    // ---------------------------------------------------------------------------
    // Card entrance: v2 uses slide-up + scale (vs v1's simple fade)
    // ---------------------------------------------------------------------------
    const cardEntranceSx = reducedMotion
        ? { opacity: appeared ? 1 : 0 }
        : {
            '@keyframes upnext-enter-v2': {
                from: { opacity: 0, transform: 'translateY(24px) scale(0.95)' },
                to:   { opacity: 1, transform: 'translateY(0) scale(1)' },
            },
            animation: `upnext-enter-v2 ${APPEAR_MS}ms ${EASING} forwards`,
        };

    // OVERDUE dot pulse (same as v1)
    const overdueDotsxProps = isOverdue && !reducedMotion
        ? {
            '@keyframes overdue-pulse': {
                '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                '50%':       { transform: 'scale(1.4)', opacity: 0.4 },
            },
            animation: 'overdue-pulse 1.4s ease-in-out infinite',
        }
        : {};

    return (
        <Box
            data-testid="upnext-card-v2"
            sx={{
                position: 'relative',
                overflow: 'hidden',
                bgcolor: '#ffffff',
                borderRadius: '16px',
                p: 3,
                minHeight: 160,
                // Full-height flex column so the action row can be pushed to the bottom
                // (mt:auto) when the action panel stretches this card tall — uses the
                // otherwise-empty vertical space instead of clustering at the top.
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)',
                ...cardEntranceSx,
            }}
        >
            {/* Mood gradient overlay — radial from top-left in action color */}
            <Box
                data-testid="mood-gradient"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '16px',
                    background: `radial-gradient(at top left, ${meta.color}14 0%, transparent 60%)`,
                    pointerEvents: 'none',
                    ...(reducedMotion
                        ? {}
                        : {
                            '@keyframes mood-fade': {
                                from: { opacity: 0 },
                                to:   { opacity: 1 },
                            },
                            opacity: 0,
                            animation: appeared
                                ? 'mood-fade 600ms 200ms cubic-bezier(0.32, 0.72, 0, 1) forwards'
                                : 'none',
                        }),
                }}
            />

            {/* Left accent strip (same as v1) */}
            <Box
                data-testid="upnext-accent-strip-v2"
                sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    bgcolor: meta.color,
                    borderRadius: '16px 0 0 16px',
                }}
            />

            {/* ── Single column (featured task only) ──────────────────── */}
            {/* The "Next up" preview pane was removed for clarity — the card is
                now one minimal column: eyebrow / title / due date / actions. */}
            <Box sx={{ position: 'relative', minWidth: 0, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>

            {/* Eyebrow */}
            <Box
                data-testid="upnext-eyebrow-v2"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}
            >
                <Box
                    data-testid="upnext-eyebrow-dot-v2"
                    data-overdue={isOverdue ? 'true' : undefined}
                    sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        bgcolor: eyebrow.color,
                        flexShrink: 0,
                        ...overdueDotsxProps,
                    }}
                />
                <Typography
                    component="span"
                    sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: eyebrow.color,
                    }}
                >
                    {eyebrow.label}
                </Typography>
            </Box>

            {/* Icon + Title — scales down when the action panel is open (narrow column)
                so the content stays clean + left-aligned in the reduced space. */}
            <Stack direction="row" alignItems="flex-start" spacing={panelOpen ? 1 : 1.5} sx={{ mb: panelOpen ? 1.5 : 2 }}>
                <Box
                    data-testid="upnext-icon-v2"
                    data-animation={reducedMotion ? 'none' : animClass}
                    sx={{
                        width: panelOpen ? 40 : 56,
                        height: panelOpen ? 40 : 56,
                        borderRadius: panelOpen ? '11px' : '14px',
                        bgcolor: `${meta.color}1A`,
                        border: `1.5px solid ${meta.color}33`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        mt: 0.25,
                        ...iconAnimSx,
                    }}
                >
                    <Iconify icon={meta.icon} width={panelOpen ? 20 : 28} height={panelOpen ? 20 : 28} sx={{ color: meta.color }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: panelOpen ? '17px' : '22px',
                            fontWeight: 700,
                            color: '#1d1d1f',
                            letterSpacing: '-0.022em',
                            lineHeight: 1.25,
                            // Allow 2-line wrap for long titles like "1-MONTH FROM CONSULTATION".
                            // wordBreak:'break-word' handles hyphenated strings without spaces.
                            wordBreak: 'break-word',
                        }}
                    >
                        {title}
                    </Typography>
                    {item.description && (
                        <Typography
                            sx={{
                                fontSize: '13px',
                                color: '#3c3c43',
                                letterSpacing: '-0.005em',
                                mt: 0.5,
                                lineHeight: 1.4,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {item.description}
                        </Typography>
                    )}
                    {/* Date + Reschedule + Assignee row — all right-side meta on ONE line:
                        [due date] · [Reschedule] · [ASSIGN TO avatars].
                        The date + Reschedule part renders only when due_at exists; the
                        assignee heads ALWAYS render (right-aligned via the flexGrow spacer),
                        so a dateless task still shows ASSIGN TO. */}
                    <Stack
                        direction={panelOpen ? 'column' : 'row'}
                        alignItems={panelOpen ? 'flex-start' : 'center'}
                        spacing={panelOpen ? 0.5 : 0.75}
                        sx={{ mt: 0.75, width: panelOpen ? '100%' : 'auto', flexWrap: 'wrap', rowGap: panelOpen ? '6px' : '4px' }}
                    >
                        {item?.due_at && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                <Iconify icon="mdi:calendar-clock" width={14} height={14} sx={{ color: '#86868b', flexShrink: 0 }} />
                                <Typography
                                    data-testid="upnext-featured-due-timestamp-v2"
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        color: '#86868b',
                                        letterSpacing: '-0.005em',
                                    }}
                                >
                                    {formatDueRelative(item.due_at)}
                                </Typography>
                                {eyebrow.overdueBy && (
                                    <Box
                                        component="span"
                                        data-testid="upnext-overdue-by-v2"
                                        sx={{
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            color: '#FF5630',
                                            bgcolor: 'rgba(255,86,48,0.08)',
                                            px: 0.75,
                                            py: 0.125,
                                            borderRadius: '5px',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Overdue by {eyebrow.overdueBy}
                                    </Box>
                                )}
                                {onChangeDueDate && (
                                    <Button
                                        data-testid="upnext-reschedule-btn-v2"
                                        size="small"
                                        aria-label="Reschedule task"
                                        startIcon={<Iconify icon="eva:edit-2-outline" width={13} height={13} />}
                                        onClick={handleOpenReschedule}
                                        sx={{
                                            color: '#6e6e73',
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            textTransform: 'none',
                                            letterSpacing: '-0.005em',
                                            bgcolor: 'transparent',
                                            borderRadius: '6px',
                                            py: 0,
                                            px: '6px',
                                            minWidth: 0,
                                            ml: 0.5,
                                            '&:hover': { color: '#1d1d1f', bgcolor: 'rgba(0,0,0,0.04)' },
                                        }}
                                    >
                                        Reschedule
                                    </Button>
                                )}
                            </Box>
                        )}
                        {/* Spacer — pushes the assignee heads to the right edge (wide
                            layout only). When the action panel is open the meta area is a
                            COLUMN, so the assignee heads sit on their OWN line underneath
                            the date — no spacer needed. */}
                        {!panelOpen && <Box sx={{ flexGrow: 1 }} />}
                        {/* Assignee profile-heads — same picker as before (AssignedToLead
                            "ASSIGN TO"). Real task → PATCH by taskId; preview item → spawn
                            from template then assign. */}
                        {item && item.taskId && practiceId && (
                            <Box data-testid="upnext-assignee-row-v2">
                                <TaskAssigneeHeads
                                    practiceId={practiceId}
                                    taskId={item.taskId}
                                    initialAssignees={item.rawTask?.assignees || []}
                                    leadId={leadId}
                                    onLeadTasksReassigned={onLeadTasksReassigned}
                                />
                            </Box>
                        )}
                        {item && !item.taskId && item._templateId && practiceId && (
                            <Box data-testid="upnext-assignee-row-v2-preview">
                                <PreviewAssigneeHeads
                                    item={item}
                                    onPreviewAssign={onPreviewAssign}
                                />
                            </Box>
                        )}
                    </Stack>
                </Box>
            </Stack>

            {/* Action row — PRIMARY "Set Next Action" on the LEFT, de-emphasized
                SECONDARY actions (Skip · Pause) on the RIGHT. Reschedule task lives
                next to the date row above, not here. When the action panel is open the
                column is narrow, so we LEFT-ALIGN the whole row (flex-start) and tighten
                the gap — primary + secondary group together on the left instead of being
                split to opposite edges.
                When the action panel is OPEN, the work happens on the RIGHT panel, so we
                HIDE this entire action row on the left — no duplicate primary CTA, no
                competing Mark/Skip/Pause. The left becomes a calm task-context card
                (icon, title, due date, reschedule, assignees). */}
            {!panelOpen && (
            <Stack
                direction="row"
                alignItems="center"
                justifyContent={panelOpen ? 'flex-start' : 'space-between'}
                spacing={panelOpen ? 1 : 1.5}
                sx={{
                    flexWrap: 'wrap',
                    rowGap: '8px',
                    // Narrow column: drop to the bottom of the card (uses the height)
                    // and indent to line up with the title (icon 40 + gap 8 = 48px).
                    mt: panelOpen ? 'auto' : 0,
                    pt: panelOpen ? 2 : 0,
                    pl: panelOpen ? '48px' : 0,
                }}
            >
                {/* Primary (left) — action button for checklist items and actionable standalone
                    tasks (WA/email/call). Hidden for method-less standalone tasks (STANDALONE_TASK)
                    — those have no panel to open, so "Mark as completed" is the only affordance. */}
                {showPrimaryButton && (
                <Tooltip
                    title={noEmailBlocked ? 'This lead has no email on file' : ''}
                    placement="top"
                    arrow
                    disableHoverListener={!noEmailBlocked}
                >
                    <span>
                        <Button
                            data-testid="upnext-action-btn-v2"
                            variant="contained"
                            disableElevation
                            disabled={noEmailBlocked}
                            endIcon={!noEmailBlocked && <Iconify icon="mdi:arrow-right" width={16} height={16} />}
                            onClick={!noEmailBlocked ? () => onActionClick && onActionClick(item) : undefined}
                            sx={{
                                ...CRM_PRIMARY_BUTTON_SX,
                                fontSize: '14px',
                                fontWeight: 700,
                                minWidth: 140,
                                py: 1,
                            }}
                        >
                            {buttonLabel}
                        </Button>
                    </span>
                </Tooltip>
                )}

                {/* Secondary (right) — subtle text/icon buttons, de-emphasized. */}
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: '4px' }}>
                    {/* Change 1: "Mark as completed" — wired to the existing handlePanelComplete path.
                        Wave A Change 2: disabled + spinner while submitting.
                        Wave A Change 3: hidden for LEAD_NEXT_ACTION (needs resolved_action payload). */}
                    {onMarkComplete && !requiresActionFirst && (
                        <Button
                            data-testid="upnext-mark-complete-btn-v2"
                            size="small"
                            disabled={submitting}
                            startIcon={submitting
                                ? <CircularProgress size={13} sx={{ color: '#34c759' }} />
                                : <Iconify icon="mdi:check-circle-outline" width={15} height={15} />}
                            onClick={() => { setSubmitting(true); onMarkComplete(item); }}
                            sx={{
                                color: '#34c759',
                                fontSize: '13px',
                                fontWeight: 500,
                                textTransform: 'none',
                                letterSpacing: '-0.01em',
                                bgcolor: 'transparent',
                                borderRadius: '6px',
                                py: '2px',
                                px: '8px',
                                minWidth: 0,
                                '&:hover': { color: '#2db14e', bgcolor: 'rgba(52,199,89,0.08)' },
                                '&.Mui-disabled': { color: 'rgba(52,199,89,0.5)' },
                            }}
                        >
                            Mark as completed
                        </Button>
                    )}
                    {/* Wave A Change 2: Skip — hidden for standalone tasks (no itemId → stuck spinner) */}
                    {!item?.isStandaloneTask && (
                    <Button
                        data-testid="upnext-skip-btn-v2"
                        size="small"
                        disabled={submitting}
                        startIcon={submitting
                            ? <CircularProgress size={13} sx={{ color: '#6e6e73' }} />
                            : <Iconify icon="mdi:skip-next-outline" width={15} height={15} />}
                        onClick={() => { setSubmitting(true); if (onSkip) onSkip(item); }}
                        sx={{
                            color: '#6e6e73',
                            fontSize: '13px',
                            fontWeight: 500,
                            textTransform: 'none',
                            letterSpacing: '-0.01em',
                            bgcolor: 'transparent',
                            borderRadius: '6px',
                            py: '2px',
                            px: '8px',
                            minWidth: 0,
                            '&:hover': { color: '#1d1d1f', bgcolor: 'rgba(0,0,0,0.04)' },
                        }}
                    >
                        Skip for now
                    </Button>
                    )}
                    {/* Wave A Change 2: Pause — hidden for standalone tasks (no itemId endpoint); disabled while submitting */}
                    {!item?.isStandaloneTask && onPause && (
                        <Button
                            data-testid="upnext-pause-btn-v2"
                            size="small"
                            disabled={submitting}
                            startIcon={<Iconify icon="mdi:pause-circle-outline" width={15} height={15} />}
                            onClick={() => onPause(item)}
                            sx={{
                                color: '#6e6e73',
                                fontSize: '13px',
                                fontWeight: 500,
                                textTransform: 'none',
                                letterSpacing: '-0.01em',
                                bgcolor: 'transparent',
                                borderRadius: '6px',
                                py: '2px',
                                px: '8px',
                                minWidth: 0,
                                '&:hover': { color: '#FFAB00', bgcolor: 'rgba(255,171,0,0.08)' },
                            }}
                        >
                            Pause
                        </Button>
                    )}
                </Stack>
            </Stack>
            )}

            {/* Assignee heads moved UP into the date+reschedule row (right-aligned,
                in line with the due date) — see the meta Stack inside the title box. */}

            </Box>{/* /single column */}

            {/* Change 3: shared RescheduleDialog (extracted to avoid divergence with PostSendExpandedCard) */}
            <RescheduleDialog
                open={rescheduleOpen}
                onClose={handleCloseReschedule}
                onConfirm={handleConfirmReschedule}
                item={item}
            />
        </Box>
    );
}

UpNextCardV2.propTypes = {
    item: PropTypes.object,
    onActionClick: PropTypes.func,
    onSkip: PropTypes.func,
    /** Called with item when user clicks "Pause" — opens pause dialog in parent. */
    onPause: PropTypes.func,
    /** Lead's email — when empty and item is an email type, CTA is blocked. */
    leadEmail: PropTypes.string,
    /** Called with (item, newDate: Date) when user confirms a due-date change. */
    onChangeDueDate: PropTypes.func,
    /** @deprecated The "Next up" preview pane was removed — retained for caller compat, no longer rendered. */
    nextItem: PropTypes.object,
    /** @deprecated No longer rendered (preview pane removed). */
    nextEmptyReason: PropTypes.oneOf(['caught_up']),
    /** @deprecated No longer rendered (preview pane removed). */
    onItemClick: PropTypes.func,
    /** Practice ID forwarded to the assignee profile-heads for PATCH calls. */
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** @deprecated Two-pane layout removed — retained for caller compat, no longer used. */
    panelOpen: PropTypes.bool,
    /** Called with (item, selectedPracticeUsers[]) for a PREVIEW item (no taskId);
        parent spawns the task from template then assigns. */
    onPreviewAssign: PropTypes.func,
    /** Change 1: called with item to mark it complete via the existing handlePanelComplete
        path — do NOT create a parallel completion, just wire to the orchestrator. */
    onMarkComplete: PropTypes.func,
    /** Lead this card's task belongs to — forwarded to TaskAssigneeHeads for the
        "Assign to all" → propagate-to-lead flow. */
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Forwarded to TaskAssigneeHeads — called after the lead's other open tasks
        are successfully reassigned. */
    onLeadTasksReassigned: PropTypes.func,
};

UpNextCardV2.defaultProps = {
    item: null,
    onActionClick: null,
    onSkip: null,
    onPause: null,
    leadEmail: undefined,
    onChangeDueDate: null,
    nextItem: null,
    nextEmptyReason: null,
    onItemClick: null,
    practiceId: null,
    panelOpen: false,
    onPreviewAssign: null,
    onMarkComplete: null,
    leadId: null,
    onLeadTasksReassigned: null,
};
